"use client";

/**
 * Styled toggle for use in settings/forms.
 *
 * Parent component is responsible for managing the state (isChecked value).
 * props.onClick should be a function that accepts the name & current checked value, and then
 * triggers a change in the parent that will pass down the new checked value.
 */

import React, { useCallback } from "react";

import styled from "styled-components";
import Flex from "../Flex";
import { getColor, getFont } from "../utils";

type Props = {
  id?: string;
  isOn: boolean;
  size?: "small" | "regular";
  disabled?: boolean;
  onToggle?: (isOn: boolean) => void;
  label?: {
    text: string;
    font?: string;
    position?: "left" | "right";
  };
};

const sizes = {
  small: {
    w: 30,
    h: 18,
    m: 3,
  },
  regular: {
    w: 40,
    h: 24,
    m: 3,
  },
};
function handleSize(size: "small" | "regular") {
  const m = sizes[size].m;
  return sizes[size].h - m * 2;
}

const ControlWrapper = styled.button<Pick<Props, "size" | "disabled">>`
  position: relative;
  display: inline-block;
  width: ${props => sizes[props.size].w}px;
  height: ${props => sizes[props.size].h}px;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const Checkbox = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:focus + span {
    box-shadow: 0 0 1px ${props => props.color};
  }
`;

const Handle = styled.span<Pick<Props, "isOn" | "disabled" | "size">>`
  position: absolute;
  cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props =>
    props.isOn ? getColor(props.theme?.primaryColor, "primary") : getColor("grayDark")};
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 34px;
  flex-shrink: 0;

  &:before {
    position: absolute;
    content: "";
    cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
    width: ${props => handleSize(props.size)}px;
    height: ${props => handleSize(props.size)}px;
    left: ${props =>
      props.isOn
        ? sizes[props.size].w - handleSize(props.size) - sizes[props.size].m
        : sizes[props.size].m}px;
    bottom: ${props => sizes[props.size].m}px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const Label = styled.label<Pick<Props["label"], "position" | "font">>`
  margin-right: ${props => (props.position === "left" ? 6 : 0)}px;
  margin-left: ${props => (props.position === "right" ? 6 : 0)}px;
  ${props => getFont(props.font, "ui.regular")};
  color: ${getColor("black")};
`;

const Switch: React.FC<Props> = ({
  id,
  isOn = false,
  size = "regular",
  onToggle,
  label = null,
  disabled = false,
  ...rest
}) => {
  const handler = useCallback(() => {
    onToggle(!isOn);
  }, [isOn, onToggle]);

  function renderLabel(render) {
    if (render && label && label.text)
      return (
        <Label
          data-testid="switch-label"
          htmlFor={id}
          font={label.font}
          position={label.position || "left"}>
          {label.text}
        </Label>
      );
    return null;
  }

  return (
    <Flex alignItems="center" data-testid={id} {...rest}>
      {renderLabel(label && (!label.position || label.position === "left"))}
      <ControlWrapper
        size={size}
        className={`${disabled ? "disabled" : ""}`}
        data-testid="switch-content"
        onClick={disabled ? null : handler}
        role="switch"
        aria-checked={isOn}
        disabled={disabled}>
        <Handle data-testid="switch-handle" disabled={disabled} isOn={isOn} size={size} />
        <Checkbox id={id} />
      </ControlWrapper>
      {renderLabel(label?.position === "right")}
    </Flex>
  );
};

export default Switch;
